/**
 * PoC Detail Modal Component
 * Shows details for approved or denied PoC documents
 */

import React from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Divider,
  Stack,
} from '@mui/material'
import {
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material'
import { PoCDocument } from '@/services/pocApi'
import { format } from 'date-fns'

interface PoCDetailModalProps {
  open: boolean
  onClose: () => void
  document: PoCDocument | null
}

const PoCDetailModal: React.FC<PoCDetailModalProps> = ({
  open,
  onClose,
  document,
}) => {
  if (!document) return null

  const isApproved = document.authorization_status === 'approved'
  const isDenied = document.authorization_status === 'denied'

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          {isApproved ? (
            <CheckCircleIcon sx={{ color: 'success.main', fontSize: 28 }} />
          ) : isDenied ? (
            <CancelIcon sx={{ color: 'error.main', fontSize: 28 }} />
          ) : null}
          <Typography variant="h6" fontWeight={600}>
            {isApproved
              ? 'Preauthorization Completed'
              : isDenied
                ? 'Preauthorization Declined'
                : 'PoC Details'}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ py: '1.5rem !important' }}>
        <Stack spacing={2.5}>
          {isApproved && (
            <>
              {document.pre_auth_id && (
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}
                    display="block"
                    mb={0.5}
                  >
                    Pre auth ID
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {document.pre_auth_id}
                  </Typography>
                </Box>
              )}

              {document.approved_date && (
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}
                    display="block"
                    mb={0.5}
                  >
                    Approval Date
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {format(new Date(document.approved_date), 'MM/dd/yyyy')}
                  </Typography>
                </Box>
              )}

              {document.authorized_no_session !== undefined && (
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}
                    display="block"
                    mb={0.5}
                  >
                    Authorized number of Sessions
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {document.authorized_no_session}
                  </Typography>
                </Box>
              )}

              {document.auth_service_period && (
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}
                    display="block"
                    mb={0.5}
                  >
                    Authorized Service Period (valid until)
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {format(
                      new Date(document.auth_service_period),
                      'MM/dd/yyyy'
                    )}
                  </Typography>
                </Box>
              )}
            </>
          )}

          {isDenied && (
            <>
              {document.denial_date && (
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}
                    display="block"
                    mb={0.5}
                  >
                    Date of Denial
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {format(new Date(document.denial_date), 'MM/dd/yyyy')}
                  </Typography>
                </Box>
              )}

              {document.denial_reason && (
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}
                    display="block"
                    mb={0.5}
                  >
                    Reason
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {document.denial_reason}
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  )
}

export default PoCDetailModal
